import {useEffect, useRef, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import AtomTextarea, {AtomTextareaResizes} from '@s-ui/react-atom-textarea'
import {useSuiContext} from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import IconArrowUpSmall from '@adv-mt/mt-svg-icons/lib/ArrowUpSmall.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import RecordingIconButton from './components/RecordingIconButton.js'

const BASE_CLASS = 'mt-NetInput'

const NET_INPUT_ID = 'net-assistant-input'
const TEXTAREA_CONTAINER_ID = `${NET_INPUT_ID}-container`
const TEXTAREA_ID = `${NET_INPUT_ID}-textarea`

const TYPING_DELAY_MS = 500
const TYPING_SPEED_MS = 40
const DELETING_SPEED_MS = 25
const PAUSE_BEFORE_DELETE_MS = 1500

const NetInput = ({
  chatId,
  onSubmit,
  disabled = false,
  placeholder,
  animatedPlaceholder = false,
  autoFocus = false
}) => {
  const {i18n, domain} = useSuiContext()
  const {track} = useAnalyticsTrack()

  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const {MESSAGE_TYPES} = config.get('COMMON').AI_ASSISTANT
  const {TRACKING_EVENT_NAMES: {NET_ASSISTANT_MICROPHONE_CLICKED} = {}} = config.get('tracking') || {}
  const {NET_INPUT_USER_DEFAULT_MESSAGE_SESSION_KEY} = config.get('COMMON').AI_ASSISTANT

  const [value, setValue] = useState('')
  const [messageType, setMessageType] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [typedPlaceholder, setTypedPlaceholder] = useState('')
  const [isAnimationPaused, setIsAnimationPaused] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)
  const currentPlaceholderIndexRef = useRef(0)
  const stopRecordingRef = useRef(null)

  // Get initial value set when expanding/shrinking chat and clean after setting on state
  useMount(() => {
    const initialValue = sessionStorage.getItem(NET_INPUT_USER_DEFAULT_MESSAGE_SESSION_KEY)

    if (initialValue) {
      setValue(initialValue)
      sessionStorage.removeItem(NET_INPUT_USER_DEFAULT_MESSAGE_SESSION_KEY)
    }
  })

  useEffect(() => {
    if (!animatedPlaceholder || !placeholder || isAnimationPaused) return

    const placeholders = Array.isArray(placeholder) ? placeholder : [placeholder]
    let index = 0
    let isDeleting = false
    let cancelled = false
    let timerId = null

    const tick = () => {
      if (cancelled) return
      const current = placeholders[currentPlaceholderIndexRef.current]

      if (!isDeleting) {
        index++
        setTypedPlaceholder(current.slice(0, index))
        if (index >= current.length) {
          isDeleting = true
          timerId = setTimeout(tick, PAUSE_BEFORE_DELETE_MS)
        } else {
          timerId = setTimeout(tick, TYPING_SPEED_MS)
        }
      } else {
        index--
        setTypedPlaceholder(current.slice(0, index))
        if (index <= 0) {
          isDeleting = false
          currentPlaceholderIndexRef.current = (currentPlaceholderIndexRef.current + 1) % placeholders.length
          timerId = setTimeout(tick, TYPING_DELAY_MS)
        } else {
          timerId = setTimeout(tick, DELETING_SPEED_MS)
        }
      }
    }

    timerId = setTimeout(tick, TYPING_DELAY_MS)

    return () => {
      cancelled = true
      clearTimeout(timerId)
    }
  }, [placeholder, animatedPlaceholder, isAnimationPaused])

  // Handle resizing of the text area
  useEffect(() => {
    const textarea = document.getElementById(TEXTAREA_ID)

    const maxLines = 4
    const lineHeight = 24 // $lh-xl

    // reset height first so when clearing input it also sets the new height
    textarea.style.height = 'auto'

    const newHeight = Math.min(textarea.scrollHeight, lineHeight * maxLines)
    textarea.style.height = newHeight + 'px'

    // This ensures the textarea scrolls to show the latest content during recording
    if (isRecording && value) {
      textarea.scrollTop = textarea.scrollHeight
    }
  }, [value, isRecording])

  const handleChange = (_event, {value: newValue}) => {
    setValue(newValue)
    setMessageType(MESSAGE_TYPES.CUSTOM)
  }

  // Handle "Enter" key behaviour
  // On desktop submit message & on mobile enter a new line
  const handleKeyDown = event => {
    if (!isMobile && event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleSubmit = () => {
    if (value.trim() && !disabled) {
      stopRecordingRef.current?.()

      onSubmit(value.trim(), messageType)
      setValue('')
      setMessageType('')

      // Reset input height after submitting
      const textarea = document.getElementById(TEXTAREA_ID)
      textarea.style.height = 'auto'
    }
  }

  const onStartRecording = () => {
    setIsRecording(true)
    setMessageType(MESSAGE_TYPES.MICRO)
    track({event: NET_ASSISTANT_MICROPHONE_CLICKED, data: {hit_information: chatId}})
  }

  const hasValue = value.trim().length > 0

  const className = cx(BASE_CLASS, {
    [`${BASE_CLASS}--isDisabled`]: disabled,
    [`${BASE_CLASS}--initialAutoFocus`]: autoFocus && !hasInteracted
  })

  return (
    <div className={className} id={NET_INPUT_ID}>
      <div className={`${BASE_CLASS}-inputContainer`} id={TEXTAREA_CONTAINER_ID}>
        <AtomTextarea
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={() => stopRecordingRef.current?.()}
          autoFocus={autoFocus}
          onFocus={() => {
            if (!animatedPlaceholder) return
            const placeholders = Array.isArray(placeholder) ? placeholder : [placeholder]
            setTypedPlaceholder(placeholders[currentPlaceholderIndexRef.current])
            setIsAnimationPaused(true)
          }}
          onBlur={() => {
            setHasInteracted(true)
            if (!animatedPlaceholder || value) return
            const placeholders = Array.isArray(placeholder) ? placeholder : [placeholder]
            currentPlaceholderIndexRef.current = (currentPlaceholderIndexRef.current + 1) % placeholders.length
            setIsAnimationPaused(false)
          }}
          disabled={disabled}
          readOnly={isRecording}
          placeholder={
            isRecording
              ? i18n.t('NET.INPUT_PRECORDING_LACEHOLDER')
              : animatedPlaceholder
              ? typedPlaceholder
              : placeholder ?? i18n.t('NET.INPUT_PLACEHOLDER')
          }
          resize={AtomTextareaResizes.NONE}
          id={TEXTAREA_ID}
          rows="1"
        />

        {hasValue ? (
          <AtomButton
            aria-label={i18n.t('NET.INPUT_SEND_MESSAGE')}
            className={`${BASE_CLASS}-sendButton`}
            color={atomButtonColors.PRIMARY}
            design={atomButtonDesigns.FLAT}
            disabled={disabled}
            leftIcon={<IconArrowUpSmall size={atomIconSizes.medium} />}
            onClick={handleSubmit}
            shape={atomButtonShapes.CIRCULAR}
            size={atomButtonSizes.SMALL}
          />
        ) : (
          <RecordingIconButton
            disabled={disabled}
            handleText={setValue}
            listening={isRecording}
            onStop={() => setIsRecording(false)}
            onStart={onStartRecording}
            stopRef={stopRecordingRef}
          />
        )}
      </div>
    </div>
  )
}

NetInput.displayName = 'NetInput'

NetInput.propTypes = {
  chatId: PropTypes.string,
  onSubmit: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  animatedPlaceholder: PropTypes.bool,
  autoFocus: PropTypes.bool
}

export {TEXTAREA_ID as NET_INPUT_ID}
export default NetInput
